import { autoUpdate } from '@floating-ui/react'
import {
  type ComponentProps,
  type RefObject,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { useOverlayPosition } from 'react-aria/useOverlayPosition'
import {
  OverlayTriggerStateContext,
  Popover,
  PopoverContext,
  useContextProps,
} from 'react-aria-components'

type Props = ComponentProps<typeof Popover> & {
  triggerRef: RefObject<Element | null>
}

export const SelectPopover = (props: Props) => {
  const localRef = useRef<HTMLElement>(null)
  const [popoverProps, popoverRef] = useContextProps(
    props,
    localRef,
    PopoverContext,
  )
  const state = useContext(OverlayTriggerStateContext)
  const isOpen = state?.isOpen ?? false
  const { triggerRef } = popoverProps
  const { updatePosition } = useOverlayPosition({
    ...popoverProps,
    targetRef: triggerRef,
    overlayRef: popoverRef,
    isOpen,
  })

  useEffect(() => {
    const trigger = triggerRef.current
    const popover = popoverRef.current
    if (!isOpen || !trigger || !popover) {
      return
    }

    // React Aria observes size changes, but a centered modal can move the input
    // without resizing it when the rest of the form finishes loading.
    return autoUpdate(trigger, popover, updatePosition, {
      ancestorScroll: false,
      ancestorResize: false,
      elementResize: false,
    })
  }, [isOpen, triggerRef, popoverRef, updatePosition])

  return <Popover {...popoverProps} ref={popoverRef} />
}
